/**
 * Oklab/Oklch, the authoring space for every physical albedo in the renderer.
 *
 * Two reasons the palettes are not hex any more. Interpolating hex means
 * interpolating in sRGB, which drags a violet-to-magenta ramp through a muddy
 * dip that no amount of stop-tweaking removes. And a renderer that computes
 * light transport needs albedo, not screen colour: `#fdfafb` is a linear
 * reflectance of 0.98, brighter than fresh snow, so it can only ever look
 * chalky under real illumination. Oklch exposes perceptual lightness directly,
 * so a stop can be placed at a chosen reflectance and read back as such.
 *
 * Conversions are Björn Ottosson's published matrices, unmodified.
 */

/** Perceptual lightness `0…1`, chroma, hue in degrees. */
export type Oklch = readonly [lightness: number, chroma: number, hueDeg: number];

export type Linear = [red: number, green: number, blue: number];

const DEGREES = Math.PI / 180;

/** Rec. 709 luminance weights, which is what "reflectance" means below. */
export function linearLuminance(rgb: Linear): number {
  return 0.2126 * rgb[0] + 0.7152 * rgb[1] + 0.0722 * rgb[2];
}

export function oklabToLinear(lightness: number, a: number, b: number): Linear {
  const longRoot = lightness + 0.3963377774 * a + 0.2158037573 * b;
  const mediumRoot = lightness - 0.1055613458 * a - 0.0638541728 * b;
  const shortRoot = lightness - 0.0894841775 * a - 1.291485548 * b;
  const long = longRoot * longRoot * longRoot;
  const medium = mediumRoot * mediumRoot * mediumRoot;
  const short = shortRoot * shortRoot * shortRoot;
  return [
    4.0767416621 * long - 3.3077115913 * medium + 0.2309699292 * short,
    -1.2684380046 * long + 2.6097574011 * medium - 0.3413193965 * short,
    -0.0041960863 * long - 0.7034186147 * medium + 1.707614701 * short,
  ];
}

export function oklchToLinear(colour: Oklch): Linear {
  const [lightness, chroma, hueDeg] = colour;
  const hue = hueDeg * DEGREES;
  return oklabToLinear(lightness, chroma * Math.cos(hue), chroma * Math.sin(hue));
}

/**
 * Interpolate two stops in Oklch, hue by the shortest arc.
 *
 * Hue has to travel the short way round or a lavender-to-magenta step at
 * 295° → 8° would sweep backwards through green.
 */
export function oklchMix(from: Oklch, to: Oklch, fraction: number): Oklch {
  let delta = (to[2] - from[2]) % 360;
  if (delta > 180) delta -= 360;
  if (delta < -180) delta += 360;
  return [
    from[0] + (to[0] - from[0]) * fraction,
    from[1] + (to[1] - from[1]) * fraction,
    from[2] + delta * fraction,
  ];
}

/**
 * Colour at a position along a stop list, interpolated in Oklch.
 *
 * Shared because the palette textures and the favicon have to agree: an icon whose
 * colours were retyped by hand would drift from the ramp it stands for the first
 * time a stop moved.
 */
export function sampleOklchStops(
  stops: ReadonlyArray<readonly [number, Oklch]>,
  position: number,
): Oklch {
  let segment = 0;
  while (segment < stops.length - 2 && position > stops[segment + 1][0]) segment += 1;
  const [low, lowColour] = stops[segment];
  const [high, highColour] = stops[segment + 1];
  const span = high - low;
  const fraction = span > 0 ? Math.min(Math.max((position - low) / span, 0), 1) : 0;
  return oklchMix(lowColour, highColour, fraction);
}

/**
 * Nearest in-gamut linear sRGB, found by shrinking chroma at fixed lightness
 * and hue. Saturated magenta at low lightness leaves sRGB, and letting a
 * negative channel through would poison the light transport downstream: a
 * negative albedo removes energy from a bounce.
 *
 * Bisection on chroma, 24 steps, so the residue is under 1e-7 of the starting
 * chroma — far below any 16-bit palette texture can carry.
 */
export function oklchToGamut(colour: Oklch): Linear {
  const direct = oklchToLinear(colour);
  if (inGamut(direct)) return direct;
  let low = 0;
  let high = colour[1];
  let best = oklchToLinear([colour[0], 0, colour[2]]);
  for (let step = 0; step < 24; step += 1) {
    const middle = (low + high) / 2;
    const candidate = oklchToLinear([colour[0], middle, colour[2]]);
    if (inGamut(candidate)) {
      best = candidate;
      low = middle;
    } else {
      high = middle;
    }
  }
  return best;
}

function inGamut(rgb: Linear): boolean {
  return (
    rgb[0] >= 0 && rgb[0] <= 1 && rgb[1] >= 0 && rgb[1] <= 1 && rgb[2] >= 0 && rgb[2] <= 1
  );
}

export function linearToSrgb(channel: number): number {
  const clamped = channel < 0 ? 0 : channel > 1 ? 1 : channel;
  return clamped <= 0.0031308 ? clamped * 12.92 : 1.055 * Math.pow(clamped, 1 / 2.4) - 0.055;
}

/** For the CSS and Canvas 2D chrome, which still speaks hex. */
export function oklchToHex(colour: Oklch): string {
  const rgb = oklchToGamut(colour);
  let hex = "#";
  for (const channel of rgb) {
    hex += Math.round(linearToSrgb(channel) * 255)
      .toString(16)
      .padStart(2, "0");
  }
  return hex;
}
